import React, { useState } from 'react';

// Render order:
//   1. avatar_url (Google / Telegram URL or local /student/profile/avatar/{id})
//   2. Initials gradient fallback (existing visual — green chip with first letter)
//
// onError swap to fallback covers external URLs that 404 (Google rotates them
// occasionally; Telegram URLs are short-lived).
//
// URL resolution: server returns either a full https URL (provider photo) or
// a path under /student/profile/avatar/{id} (uploaded avatar). The path-based
// URL needs to go through the API base — the React dev server doesn't proxy
// it because it doesn't start with /api/. We prepend the API base here so
// the same shape works in dev (proxied through /api) and prod (nginx).
const API_BASE = process.env.REACT_APP_API_URL || '/api';

function resolveAvatarUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return url;
  if (url.startsWith('/')) return API_BASE + url;
  return url;
}

interface AvatarProps {
  url?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
  rounded?: boolean;
}

const initialsFor = (name?: string | null): string => {
  if (!name) return 'S';
  const trimmed = name.trim();
  if (!trimmed) return 'S';
  return trimmed.charAt(0).toUpperCase();
};

const Avatar: React.FC<AvatarProps> = ({
  url,
  name,
  size = 40,
  className,
  rounded = true,
}) => {
  const [broken, setBroken] = useState(false);
  const initial = initialsFor(name);

  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: rounded ? '50%' : 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #4a7c10, #65a30d)',
    color: 'white',
    fontSize: Math.round(size * 0.42),
    fontWeight: 600,
    lineHeight: 1,
    flexShrink: 0,
  };

  if (url && !broken) {
    return (
      <span className={className} style={baseStyle}>
        <img
          src={resolveAvatarUrl(url)}
          alt={name || 'avatar'}
          onError={() => setBroken(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          referrerPolicy="no-referrer"
        />
      </span>
    );
  }

  return (
    <span className={className} style={baseStyle} aria-label={name || 'avatar'}>
      {initial}
    </span>
  );
};

export default Avatar;
